import { z } from "zod";
import { VisitType } from "@/api/enums";
import type { Endpoint } from "./types";

// ── Zod Schemas (runtime validation for complex API responses) ─────

export const specialistSlotSchema = z.object({
  id: z.string(),
  dateTime: z.string(),
  timeZone: z.string(),
});

export const specialistPracticeSchema = z.object({
  practiceName: z.string().nullable(),
  practiceLocationId: z.string().nullable(),
  practiceAddress: z.string().nullable(),
  practiceAddress2: z.string().nullable(),
  practiceState: z.string().nullable(),
  practiceCity: z.string().nullable(),
  practiceZipCode: z.string().nullable().optional(),
  practicePhoneNumber: z.string().nullable().optional(),
  practiceLatitude: z.number(),
  practiceLongitude: z.number(),
  practiceDistance: z.number(),
  defaultFee: z.number(),
  practiceWebSiteUrl: z.string(),
  specialistInPersonPricing: z.number().optional(),
  specialties: z.string(),
  locationUrl: z.string(),
  directionUrl: z.string(),
  slug: z.string(),
  AvailableSlots: z.array(specialistSlotSchema),
});

export const specialistTelemedicineSchema = z.object({
  specialistTelemedicinePricing: z.number().optional(),
  AvailableSlots: z.array(specialistSlotSchema),
});

export const specialistSchema = z.object({
  specialistId: z.string(),
  specialistFirstName: z.string().nullable().optional(),
  specialistLastName: z.string().nullable().optional(),
  specialistSuffix: z.string().nullable().optional(),
  specialistTitle: z.string().nullable().optional(),
  specialistProfilePicture: z.string().nullable().optional(),
  specialistIsPayingDynamicPricing: z.boolean().optional(),
  specialistVisitType: z.enum(VisitType).nullable(),
  isRecommended: z.boolean(),
  practices: z.array(specialistPracticeSchema),
  telemedicine: specialistTelemedicineSchema,
});

export const specialistLocationSchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  address2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  zipCode: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  latLong: z
    .object({
      latitude: z.number(),
      longitude: z.number(),
      state: z.string().nullable().optional(),
      stateCode: z.string().nullable().optional(),
    })
    .optional(),
  locationUrl: z.string().nullable().optional(),
  directionUrl: z.string().nullable().optional(),
});

export const specialistProfileSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  title: z.string().nullable().optional(),
  suffix: z.string().nullable().optional(),
  practiceName: z.string(),
  practiceWebSite: z.string().nullable().optional(),
  profilePictureUrl: z.string(),
  specialties: z.string(),
  boardCertifications: z.array(z.string()).optional(),
  about: z.string().nullable().optional(),
  hospitalAffiliation: z.array(z.string()).optional(),
  locationsInPerson: z.array(specialistLocationSchema).optional(),
  visitTypeAvailability: z.string(),
  inPersonPricing: z.number(),
  telemedicinePricing: z.number(),
  slug: z.string(),
});

const paginationSchema = z.object({
  total: z.number(),
  take: z.coerce.number(),
  skip: z.coerce.number(),
  hasMore: z.boolean(),
});

const specialistsListResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string(),
    data: z.array(specialistSchema),
    pagination: paginationSchema,
  })
  .transform((r) => ({ data: r.data, pagination: r.pagination }));

const specialistProfileResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string(),
    data: specialistProfileSchema,
  })
  .transform((r) => r.data);

export const specialistsRequestSchema = z.object({
  zipCode: z.string(),
  specialty: z.string(),
  visitType: z.enum(VisitType).optional(),
  priceRange: z.number().optional(),
  distanceRange: z.number().optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
});

// ── Dropdown Schemas (admin propose-new-doctor flow) ────────────────

export const specialistDropdownLocationSchema = z.object({
  id: z.string(),
  practiceName: z.string().nullable(),
  address: z.string().nullable(),
  address2: z.string().nullable(),
  state: z.string().nullable(),
  city: z.string().nullable(),
  zipCode: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

export const specialistDropdownSchema = z.object({
  specialistId: z.string(),
  specialistSlug: z.string(),
  specialistFirstName: z.string().nullable().optional(),
  specialistLastName: z.string().nullable().optional(),
  specialistSuffix: z.string().nullable().optional(),
  specialistTitle: z.string().nullable().optional(),
  specialistInPersonPricing: z.number().nullable().optional(),
  specialistTelemedicinePricing: z.number().nullable().optional(),
  specialistVisitType: z.enum(VisitType).nullable(),
  location: specialistDropdownLocationSchema.nullable(),
  distance: z.number().optional(),
});

const specialistsDropdownResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string(),
    data: z.array(specialistDropdownSchema),
  })
  .transform((r) => r.data);

export const specialistsDropdownRequestSchema = z.object({
  specialty: z.string(),
  zipCode: z.string(),
  visitType: z.enum(VisitType).optional(),
});

// ── Inferred Types ─────────────────────────────────────────────────

export type SpecialistSlot = z.infer<typeof specialistSlotSchema>;
export type SpecialistPractice = z.infer<typeof specialistPracticeSchema>;
export type SpecialistTelemedicine = z.infer<
  typeof specialistTelemedicineSchema
>;
export type SpecialistsDetails = z.infer<typeof specialistSchema>;
export type SpecialistsListResponse = z.infer<typeof specialistsListResponseSchema>;
export type SpecialistLocation = z.infer<typeof specialistLocationSchema>;
export type SpecialistProfile = z.infer<typeof specialistProfileSchema>;
export type SpecialistsRequest = z.infer<typeof specialistsRequestSchema>;
export type SpecialistDropdownItem = z.infer<typeof specialistDropdownSchema>;
export type SpecialistDropdownLocation = z.infer<
  typeof specialistDropdownLocationSchema
>;
export type SpecialistsDropdownRequest = z.infer<
  typeof specialistsDropdownRequestSchema
>;

// ── Endpoints ──────────────────────────────────────────────────────

export const specialistEndpoints = {
  list: {
    method: "GET",
    path: "/specialists",
    schema: specialistsListResponseSchema,
  } as Endpoint,

  dropdown: {
    method: "GET",
    path: "/specialists/dropdown-by-location",
    schema: specialistsDropdownResponseSchema,
    auth: true,
  } as Endpoint,

  profile: (specialistSlug: string, practiceSlug: string): Endpoint => ({
    method: "GET",
    path: `/specialists/${specialistSlug}/${practiceSlug}`,
    schema: specialistProfileResponseSchema,
  }),
} as const;
